import React, { useMemo } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Grid,
  Stack,
  Typography,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import { useQuery } from "@apollo/client";
import { useNavigate, useParams } from "react-router-dom";
import Header from "components/Header";
import { getGraphQLErrorMessage } from "utils/graphqlError";
import {
  GET_FUNDS_DRIVE_CONTRIBUTIONS,
  GET_FUNDS_DRIVES,
  GET_FUNDS_DRIVE_MEMBER_STATUS,
} from "graphql/fundsDrives";

const formatDate = (value) => (value ? new Date(value).toLocaleString() : "-");
const formatKes = (value) => `KES ${(Number(value) || 0).toLocaleString()}`;

const SummaryCard = ({ label, value, tone = "default" }) => (
  <Card variant="outlined" sx={{ borderRadius: 3, height: "100%" }}>
    <CardContent>
      <Typography variant="body2" color="text.secondary">
        {label}
      </Typography>
      <Typography
        variant="h4"
        mt={1}
        fontWeight={800}
        color={tone === "success" ? "success.main" : tone === "warning" ? "warning.main" : "inherit"}
      >
        {value}
      </Typography>
    </CardContent>
  </Card>
);

const FundsDriveDetails = () => {
  const navigate = useNavigate();
  const { driveId } = useParams();
  const parsedDriveId = Number(driveId);

  const {
    data: drivesData,
    loading: driveLoading,
    error: driveError,
  } = useQuery(GET_FUNDS_DRIVES, {
    variables: { includeInactive: true },
    fetchPolicy: "cache-and-network",
  });

  const {
    data: contributionsData,
    loading: contributionsLoading,
    error: contributionsError,
    refetch: refetchContributions,
  } = useQuery(GET_FUNDS_DRIVE_CONTRIBUTIONS, {
    variables: { fundsDriveId: parsedDriveId },
    skip: !parsedDriveId,
    fetchPolicy: "cache-and-network",
  });

  const {
    data: statusData,
    loading: statusLoading,
    error: statusError,
    refetch: refetchStatus,
  } = useQuery(GET_FUNDS_DRIVE_MEMBER_STATUS, {
    variables: { fundsDriveId: parsedDriveId },
    skip: !parsedDriveId,
    fetchPolicy: "cache-and-network",
  });

  const drive = useMemo(
    () => (drivesData?.fundsDrives || []).find((d) => Number(d.id) === parsedDriveId),
    [drivesData, parsedDriveId]
  );

  const contributions = useMemo(() => contributionsData?.fundsDriveContributions || [], [contributionsData]);
  const memberStatusRows = useMemo(() => statusData?.fundsDriveMemberStatus || [], [statusData]);

  const sentMembers = memberStatusRows.filter((r) => r.hasSent).length;
  const unsentMembers = memberStatusRows.filter((r) => !r.hasSent).length;

  const contributionsColumns = [
    { field: "senderName", headerName: "Sender", flex: 0.9 },
    { field: "recipientName", headerName: "Recipient", flex: 0.9 },
    { field: "amount", headerName: "Amount", flex: 0.65, valueFormatter: ({ value }) => formatKes(value) },
    { field: "transactionReference", headerName: "Reference", flex: 0.9 },
    {
      field: "transactionDateTime",
      headerName: "Transfer Date",
      flex: 0.9,
      valueFormatter: ({ value }) => formatDate(value),
    },
    {
      field: "recipientConfirmed",
      headerName: "Confirmed",
      flex: 0.7,
      renderCell: ({ value }) => (
        <Chip size="small" label={value ? "Confirmed" : "Pending"} color={value ? "success" : "warning"} />
      ),
    },
    {
      field: "confirmationMessage",
      headerName: "Message",
      flex: 1.8,
      renderCell: ({ value }) => (
        <Typography variant="body2" noWrap title={value || ""}>
          {value || "-"}
        </Typography>
      ),
    },
  ];

  const memberStatusColumns = [
    { field: "memberName", headerName: "Member", flex: 1.1 },
    {
      field: "hasSent",
      headerName: "Status",
      flex: 0.8,
      renderCell: ({ value }) => (
        <Chip
          size="small"
          color={value ? "success" : "default"}
          variant={value ? "filled" : "outlined"}
          label={value ? "Sent" : "Not Sent"}
        />
      ),
    },
    { field: "numberOfTransfers", headerName: "Transfers", flex: 0.7 },
    {
      field: "totalSentAmount",
      headerName: "Total Sent",
      flex: 0.8,
      valueFormatter: ({ value }) => formatKes(value),
    },
    {
      field: "lastTransferDate",
      headerName: "Last Transfer",
      flex: 1,
      valueFormatter: ({ value }) => formatDate(value),
    },
  ];

  return (
    <Box m="1.5rem 2.5rem">
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
        <Header
          title={drive?.title ? `FUNDS DRIVE • ${drive.title}` : "FUNDS DRIVE DETAILS"}
          subtitle="Contribution ledger + member status matrix for this fundraising drive."
        />
        <Stack direction="row" spacing={1}>
          <Button variant="outlined" onClick={() => navigate("/funds-drives")}>Back</Button>
          <Button
            variant="contained"
            onClick={async () => {
              await Promise.all([refetchContributions(), refetchStatus()]);
            }}
          >
            Refresh
          </Button>
        </Stack>
      </Stack>

      {(driveLoading || contributionsLoading || statusLoading) && (
        <Stack alignItems="center" py={5}>
          <CircularProgress size={28} />
        </Stack>
      )}

      {(driveError || contributionsError || statusError) && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {getGraphQLErrorMessage(driveError || contributionsError || statusError)}
        </Alert>
      )}

      {!driveLoading && !driveError && !drive && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          This drive was not found or you do not have access to it.
        </Alert>
      )}

      {drive && (
        <>
          <Grid container spacing={2} mb={2}>
            <Grid item xs={12} md={4}>
              <SummaryCard label="Recipient" value={drive.recipientName || `Member #${drive.recipientUserId}`} />
            </Grid>
            <Grid item xs={12} md={2}>
              <SummaryCard label="Status" value={drive.isActive ? "Active" : "Inactive"} tone={drive.isActive ? "success" : "warning"} />
            </Grid>
            <Grid item xs={12} md={2}>
              <SummaryCard label="Contribution Count" value={String(drive.totalContributionCount || 0)} />
            </Grid>
            <Grid item xs={12} md={2}>
              <SummaryCard label="Total Raised" value={formatKes(drive.totalContributedAmount)} />
            </Grid>
            <Grid item xs={12} md={2}>
              <SummaryCard label="Window" value={`${drive.startDate ? new Date(drive.startDate).toLocaleDateString() : "Any"} - ${drive.endDate ? new Date(drive.endDate).toLocaleDateString() : "Open"}`} />
            </Grid>
          </Grid>

          <Grid container spacing={2}>
            <Grid item xs={12} md={8}>
              <Card variant="outlined" sx={{ borderRadius: 3 }}>
                <CardContent>
                  <Typography variant="h5" fontWeight={700} mb={1.5}>
                    Drive Contributions
                  </Typography>
                  {contributions.length === 0 ? (
                    <Alert severity="info">No contributions submitted for this drive yet.</Alert>
                  ) : (
                    <Box height="60vh">
                      <DataGrid
                        rows={contributions}
                        columns={contributionsColumns}
                        getRowId={(row) => row.id}
                        disableRowSelectionOnClick
                        pageSizeOptions={[10, 20, 50]}
                        initialState={{ pagination: { paginationModel: { pageSize: 10 } } }}
                      />
                    </Box>
                  )}
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={4}>
              <Card variant="outlined" sx={{ borderRadius: 3, mb: 2 }}>
                <CardContent>
                  <Typography variant="h5" fontWeight={700} mb={1}>
                    Member Status Matrix
                  </Typography>
                  <Typography variant="body2" color="text.secondary" mb={2}>
                    Who has sent vs has not sent, with totals and latest transfer per member.
                  </Typography>
                  <Stack direction="row" spacing={1} mb={2}>
                    <Chip label={`Sent: ${sentMembers}`} color="success" />
                    <Chip label={`Not Sent: ${unsentMembers}`} variant="outlined" />
                  </Stack>

                  {memberStatusRows.length === 0 ? (
                    <Alert severity="info">No member status rows available yet.</Alert>
                  ) : (
                    <Box height="52vh">
                      <DataGrid
                        rows={memberStatusRows}
                        columns={memberStatusColumns}
                        getRowId={(row) => row.memberUserId}
                        disableRowSelectionOnClick
                        hideFooterSelectedRowCount
                        pageSizeOptions={[10, 20, 50]}
                        initialState={{ pagination: { paginationModel: { pageSize: 10 } } }}
                      />
                    </Box>
                  )}
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </>
      )}
    </Box>
  );
};

export default FundsDriveDetails;
